import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Button, EmptyState, Skeleton, Toggle } from '@/shared/ui'
import { useBotMemory, useBotSettings, useDeleteMemory, usePatchSettings } from './hooks/useBot'
import type { BotMemoryItem } from './api/botApi'

// Badge tone per bot.UserMemory.kind (CK_BotUserMemory_kind). Literal map (not
// imported from Badge — its Tone union isn't exported), mirroring AnswerCard's
// STATUS_TONE so this stays structurally assignable to Badge's `tone` prop.
const KIND_TONE = {
  preference: 'primary',
  fact: 'info',
  pinned: 'success',
  // neutral, not warn/danger: an observed style profile isn't a status —
  // preference/fact/pinned are all things the user stated, style is the
  // one kind the bot infers itself, so it gets the "no status implied" tone.
  style: 'neutral',
} as const

function formatCreatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch {
    return ''
  }
}

interface MemoryRowProps {
  item: BotMemoryItem
  onDelete: () => void
  deleting: boolean
}

function MemoryRow({ item, onDelete, deleting }: MemoryRowProps) {
  const { t } = useTranslation('bot')
  return (
    <li className="rounded-2xl border border-outline-variant bg-surface-container-lowest p-3 flex items-start justify-between gap-3">
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          <Badge tone={KIND_TONE[item.kind]}>{t(`memory.kind.${item.kind}`)}</Badge>
          <span className="text-[10px] text-on-surface-variant">{formatCreatedAt(item.createdAt)}</span>
        </div>
        <p className="text-sm text-on-surface whitespace-pre-wrap">{item.content}</p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        icon="delete"
        aria-label={`${t('memory.delete')}: ${item.content.slice(0, 40)}`}
        onClick={onDelete}
        loading={deleting}
      />
    </li>
  )
}

/**
 * "Gedächtnis" tab body (Design Spec §7/§9). Flat data surface — the tab
 * itself lives inside BotPanel/BotCompareDock's glass chrome, so nothing
 * here reaches for GlassCard.
 */
export function MemoryTab() {
  const { t } = useTranslation('bot')
  const { data: items, isLoading } = useBotMemory()
  const { data: settings } = useBotSettings()
  const deleteMemory = useDeleteMemory()
  const patchSettings = usePatchSettings()
  const [error, setError] = useState<string | null>(null)

  function handleDelete(id: number) {
    setError(null)
    deleteMemory.mutate(id, { onError: () => setError(t('errors.generic')) })
  }

  function handleToggle(checked: boolean) {
    setError(null)
    patchSettings.mutate({ memoryEnabled: checked }, { onError: () => setError(t('errors.generic')) })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Toggle
          checked={settings?.memoryEnabled ?? true}
          onChange={handleToggle}
          disabled={!settings || patchSettings.isPending}
          label={t('memory.toggleLabel')}
        />
        <p className="text-xs text-on-surface-variant">{t('memory.explain')}</p>
      </div>

      {error && <p role="alert" className="text-xs text-error">{error}</p>}

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : !items || items.length === 0 ? (
        <EmptyState icon="psychology" title={t('memory.empty.title')} description={t('memory.empty.description')} />
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <MemoryRow
              key={item.id}
              item={item}
              onDelete={() => handleDelete(item.id)}
              deleting={deleteMemory.isPending && deleteMemory.variables === item.id}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

export default MemoryTab
